/**
 * Column choice, sort and filters for a DataTable.
 *
 * Extracted from LeadsWorkspace, which had all of this inline. It is not
 * complicated code, but it is fiddly in the same way twice — a stored column
 * list has to survive a column being renamed or removed in a later release, and
 * a filter toggle has to drop the key rather than leave an empty array behind,
 * or "any filters active?" starts answering yes to nothing.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ColumnFilters, SortDir } from '@/shared/components/DataTable'

interface Options<K extends string> {
  /** localStorage key for the column choice. Omit to keep it per-session. */
  storageKey?: string
  /** Every legal column key, used to discard stale entries on read. */
  allKeys: readonly K[]
  defaults: K[]
  initialSort: string
  initialDir?: SortDir
  /** Sort keys that should start descending — scores, counts, money. */
  descendingFirst?: readonly string[]
}

function load<K extends string>(storageKey: string | undefined, allKeys: readonly K[], defaults: K[]): K[] {
  if (!storageKey) return defaults
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return defaults
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return defaults
    // A column removed in a later release must not resurrect itself from a
    // stored preference, and an empty result means the stored list is entirely
    // stale — fall back rather than render a table with one column.
    const valid = parsed.filter((k): k is K => allKeys.includes(k as K))
    return valid.length ? valid : defaults
  } catch {
    return defaults
  }
}

export function useTableState<K extends string>({
  storageKey,
  allKeys,
  defaults,
  initialSort,
  initialDir = 'asc',
  descendingFirst = [],
}: Options<K>) {
  const [columns, setColumns] = useState<K[]>(() => load(storageKey, allKeys, defaults))
  const [showPicker, setShowPicker] = useState(false)
  const [sortBy, setSortBy] = useState<string>(initialSort)
  const [sortDir, setSortDir] = useState<SortDir>(initialDir)
  const [filters, setFilters] = useState<ColumnFilters<K>>({})
  const [openFilter, setOpenFilter] = useState<K | null>(null)

  useEffect(() => {
    if (!storageKey) return
    localStorage.setItem(storageKey, JSON.stringify(columns))
  }, [storageKey, columns])

  const toggleColumn = useCallback(
    (key: K) => {
      // Kept in `allKeys` order rather than click order, so the table does not
      // reshuffle itself depending on which box was ticked first.
      setColumns((cur) =>
        cur.includes(key)
          ? cur.filter((k) => k !== key)
          : allKeys.filter((k) => k === key || cur.includes(k)),
      )
    },
    [allKeys],
  )

  const onSort = useCallback(
    (key: string) => {
      if (key === sortBy) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
      else {
        setSortBy(key)
        setSortDir(descendingFirst.includes(key) ? 'desc' : 'asc')
      }
    },
    [sortBy, descendingFirst],
  )

  const toggleFilter = useCallback((key: K, value: string) => {
    setFilters((cur) => {
      const selected = cur[key] ?? []
      const next = selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
      // Drop the key entirely rather than leaving `[]` behind — an empty array
      // means "all", and counting it as an active filter would light up the
      // clear button with nothing to clear.
      const out = { ...cur }
      if (next.length) out[key] = next
      else delete out[key]
      return out
    })
  }, [])

  const clearFilter = useCallback((key: K) => {
    setFilters((cur) => {
      const out = { ...cur }
      delete out[key]
      return out
    })
    setOpenFilter(null)
  }, [])

  const clearAllFilters = useCallback(() => setFilters({}), [])

  const filterCount = useMemo(
    () => Object.values(filters).reduce((n: number, v) => n + ((v as string[] | undefined)?.length ?? 0), 0),
    [filters],
  )

  return {
    columns,
    setColumns,
    toggleColumn,
    showPicker,
    setShowPicker,
    sortBy,
    sortDir,
    onSort,
    filters,
    toggleFilter,
    clearFilter,
    clearAllFilters,
    filterCount,
    openFilter,
    setOpenFilter,
  }
}
